// src/components/GroupsPage.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import { v4 as uuidv4 } from 'uuid'; // For generating part of the code

// Accept groups and onAddGroup handler as props
function GroupsPage({ groups: initialGroups, onAddGroup }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(initialGroups || []); // Use prop or default to empty array
  const [filterText, setFilterText] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false); // State for modal visibility
  // Remove group_code from initial state for the form
  const [newGroupData, setNewGroupData] = useState({ name: '' }); // State for new group form

  // Update local state if the prop changes (e.g., after adding/deleting)
  React.useEffect(() => {
    if (initialGroups) {
      console.log('[GroupsPage] Updating local groups state with new groups:', initialGroups.length);
      setGroups(initialGroups);
    }
  }, [initialGroups]);

  const filteredGroups = useMemo(() => {
    if (!filterText) return groups;
    return groups.filter(group =>
      group.name.toLowerCase().includes(filterText.toLowerCase()) ||
      (group.group_code && group.group_code.toLowerCase().includes(filterText.toLowerCase())) // Filter by code too
    );
  }, [groups, filterText]);

  // --- Add Group Modal Logic ---
  const openAddModal = () => {
    setNewGroupData({ name: filterText }); // Initialize with current filter text
    setIsAddModalOpen(true);
  };
  const closeAddModal = () => setIsAddModalOpen(false);

  const handleAddGroupInputChange = (e) => {
    const { name, value } = e.target;
    // Only update the 'name' field
    if (name === 'name') {
      setNewGroupData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Helper function to generate group code from name
  const generateGroupCode = (name) => {
    if (!name) return `group-${uuidv4().substring(0, 4)}`; // Fallback if name is empty
    const baseCode = name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, '');   // Trim leading/trailing hyphens
    // Limit length and add random suffix for uniqueness
    const shortSuffix = uuidv4().substring(0, 4);
    return `${baseCode.substring(0, 15)}-${shortSuffix}`; // Example: limit base name part length
  };

  const handleSaveNewGroup = async (e) => {
    e.preventDefault();
    const trimmedName = newGroupData.name.trim();
    if (!trimmedName) { // Check only for name now
      alert('Group name is required.');
      return;
    }

    // Generate the group code automatically
    const generatedCode = generateGroupCode(trimmedName);

    // Prepare data for the handler, including the generated code
    const dataToSend = {
      name: trimmedName,
      group_code: generatedCode
    };

    // Call the handler passed from App.jsx
    if (typeof onAddGroup === 'function') {
      try {
        console.log("Sending group data to handler:", dataToSend);
        const newGroup = await onAddGroup(dataToSend); // Pass { name, group_code }
        console.log("Group created successfully:", newGroup);
        console.log("Current groups prop length:", initialGroups?.length);

        // Close the modal
        closeAddModal();
      } catch (error) {
        console.error("Error creating group:", error);
        alert(`Error creating group: ${error.message}`);
      }
    } else {
      console.warn("onAddGroup function not provided.");
      alert("Error: Could not add group. (Missing handler)");
    }
  };
  // --- End Add Group Modal Logic ---

  return (
    <div>
      {/* Filter and Add Button Bar */}
      <Card className="mb-6 p-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="w-full sm:w-auto sm:flex-1">
            <Input
              type="text"
              placeholder="Filter groups by name or code..." // Updated placeholder
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full"
            />
          </div>
          <Button
            variant="primary"
            className="text-sm py-2 px-4 flex items-center space-x-1 w-full sm:w-auto"
            onClick={openAddModal} // <-- Open the modal
          >
            <span className="text-lg font-bold leading-none mr-1">+</span>
            <span>Add Group</span>
          </Button>
        </div>
      </Card>

      {/* Groups Table Card */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Code</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"># Students</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredGroups.length > 0 ? (
                  filteredGroups.map((group) => (
                    <tr
                      key={group.id}
                      onClick={() => navigate(`/setup/groups/${group.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{group.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.group_code || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Array.isArray(group.students) ? group.students.length : 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-500 py-4">
                      {filterText ? 'No groups match your filter.' : 'No groups defined yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Add Group Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeAddModal} title="Add New Group">
        <form onSubmit={handleSaveNewGroup} className="space-y-4">
          <div>
            <label htmlFor="add-group-name" className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
            <Input
              id="add-group-name"
              name="name" // Matches state key
              value={newGroupData.name}
              onChange={handleAddGroupInputChange}
              placeholder="e.g., Cinematography Year 1"
              required
              autoFocus
            />
          </div>
          {/* Group Code Input Removed */}
          {/*
                    <div>
                        <label htmlFor="add-group-code" className="block text-sm font-medium text-gray-700 mb-1">Group Code *</label>
                        <Input
                            id="add-group-code"
                            name="group_code" // Matches state key and API requirement
                            value={newGroupData.group_code}
                            onChange={handleAddGroupInputChange}
                            placeholder="e.g., CIN-25A (Unique)"
                            required
                        />
                         <p className="text-xs text-gray-500 mt-1">Must be unique within the institution.</p>
                    </div>
                     */}
          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={closeAddModal}>Cancel</Button>
            <Button type="submit" variant="primary">Add Group</Button>
          </div>
        </form>
      </Modal>

    </div>
  );
}

export default GroupsPage;
